import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  DatePicker,
  Tabs,
  Table,
  Spin,
  Tag,
  Collapse,
  Divider,
  Segmented,
  Popover
} from 'antd';
import { Bar, Line, Doughnut, Pie } from 'react-chartjs-2';
import 'chartjs-plugin-zoom';
import {
  notificationsAtom,
  dashboardAnalyticsHeaderAtom,
  dashboardAnalyticsDataAtom,
  dashboardAnalyticsLoadedAtom,
  dashboardAnalyticsShouldLoadAtom
} from '../../../../atoms/Atoms';
import { percentage, thousand, toLocalTime } from '../../../../functions';
import api from '../../../../api/api';
import SummaryCardComparison from '../../../shared-components/SummaryCardComparison';
import './dashboard.css'

function AuthorityView() {
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [dashboardAnalyticsHeader, setDashboardAnalyticsHeader] = useRecoilState(dashboardAnalyticsHeaderAtom);
  const [dashboardAnalyticsData, setDashboardAnalyticsData] = useRecoilState(dashboardAnalyticsDataAtom);
  const [dashboardAnalyticsShouldLoad, setDashboardAnalyticsShouldLoad] = useRecoilState(dashboardAnalyticsShouldLoadAtom);
  const [data, setData] = useState(null);
  const [operators, setOperators] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [groupedByOperator, setGroupedByOperator] = useState(true);

  function getOperators() {
    api.get('dashboard/operators')
    .then((response) => {
      if (response.data.success) {
        setOperators(response.data.data);
      }
    })
    .catch((error) => {
      // Left empty
    });
  }

  function getRoutes() {
    api.get('dashboard/routes')
    .then((response) => {
      if (response.data.success) {
        setRoutes(response.data.data);
      }
    })
    .catch((error) => {
      // Left empty
    });
  }

  function getData() {
    setDashboardAnalyticsHeader({
      ...dashboardAnalyticsHeader,
      loading: true
    });
    setData(null);
    const startDate = dayjs(dashboardAnalyticsHeader.start);
    const endDate = dayjs(dashboardAnalyticsHeader.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api.post('dashboard/authority', {
      StartDate: startString,
      EndDate: endString,
      OperatorId: dashboardAnalyticsHeader.operatorId,
      RouteId: dashboardAnalyticsHeader.routeId, 
    })
    .then((response) => {
      setDashboardAnalyticsHeader({
        ...dashboardAnalyticsHeader,
        loading: false
      });
      if (response.data.success) {
        const tempRoutes = [...response.data.data.problematicRoutes]
        for (let index = 0; index < tempRoutes.length; index++) {
          const route = tempRoutes[index];
          const filteredOperators = _.filter(operators, (o) => o.operatorId === route.operatorId);
          if (filteredOperators.length > 0) {
            route.operatorName = filteredOperators[0].operatorName;
          }
          const filteredRoutes = _.filter(routes, (r) => r.routeId === route.routeId);
          if (filteredRoutes.length > 0) {
            route.routeName = filteredRoutes[0].routeName;
          }
        }
        setData({
          ...response.data.data,
          problematicRoutes: tempRoutes
        });
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setDashboardAnalyticsHeader({
        ...dashboardAnalyticsHeader,
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    });
  }

  useEffect(() => {
    if (dashboardAnalyticsShouldLoad.page1) {
      setDashboardAnalyticsHeader({
        ...dashboardAnalyticsHeader,
        loading: true
      });
      setTimeout(() => getData(), 500);
      setDashboardAnalyticsShouldLoad({
        ...dashboardAnalyticsShouldLoad,
        page1: false
      });
    }
  }, [dashboardAnalyticsShouldLoad.page1]);

  useEffect(() => {
    if (data?.problematicRoutes) {
      const tempRoutes = [...data?.problematicRoutes]
      for (let index = 0; index < tempRoutes.length; index++) {
        const route = tempRoutes[index];
        const tempOperators = _.filter(operators, (o) => o.operatorId === route.operatorId);
        if (tempOperators.length > 0) {
          route.operatorName = tempOperators[0].operatorName;
        }
        const filteredRoutes = _.filter(routes, (r) => r.routeId === route.routeId);
        if (filteredRoutes.length > 0) {
          route.routeName = filteredRoutes[0].routeName;
        }
      }
      setData({
        ...data,
        problematicRoutes: tempRoutes
      });
    }
  }, [operators, routes]);

  useEffect(() => {
    getOperators();
    getRoutes();
  }, []);

  return (
    <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
      <div className="bg-white shadow-lg p-2 col-span-1 md:col-span-1 lg:col-span-2">
        <Spin spinning={dashboardAnalyticsHeader.loading}>
          <p className="text-xl">{t('compliance_status')}</p>
          <div className="traffic-light-container horizontal mx-auto mt-2">
            <div className={`traffic-circle ${data?.status === 0 ? 'col3' : ''}`} color="col3" />
            <div className={`traffic-circle ${data?.status === 1 ? 'col2' : ''}`} color="col2" />
            <div className={`traffic-circle ${data?.status === 2 ? 'col1' : ''}`} color="col1" />
          </div>
          <div className="italic text-center mt-2">
            <p className={data?.status === 0 ? '' : 'hidden'}>{t('good_compliance_status')}</p>
            <p className={data?.status === 1 ? '' : 'hidden'}>{t('medium_compliance_status')}</p>
            <p className={data?.status === 2 ? '' : 'hidden'}>{t('bad_compliance_status')}</p>
          </div>
        </Spin>
      </div>
      <div className="bg-white shadow-lg p-2 col-span-1 md:col-span-1 lg:col-span-4">
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <SummaryCardComparison
            hideBackground
            border
            loading={dashboardAnalyticsHeader.loading}
            title={t('scheduled_trips_operated')}
            value1={percentage(data?.schedulesTripsOperatedKpi * 100)}
          />
          <SummaryCardComparison
            hideBackground
            border
            loading={dashboardAnalyticsHeader.loading}
            title={t('on_time_performance')}
            value1={percentage(data?.onTimePerformanceKpi * 100)}
          />
          <SummaryCardComparison
            hideBackground
            border
            loading={dashboardAnalyticsHeader.loading}
            title={t('cancelled_trips')}
            value1={thousand(data?.cancelledTripsKpi)}
          />
          <SummaryCardComparison
            hideBackground
            border
            loading={dashboardAnalyticsHeader.loading}
            title={t('fleet_available')}
            value1={percentage(data?.fleetAvailableKpi * 100)}
          />
        </div>
      </div>
      <div className="vh-50 bg-white shadow-lg p-2 col-span-1 md:col-span-2 lg:col-span-6">
        <Spin spinning={dashboardAnalyticsHeader.loading}>
          <Line
            height={350}
            data={{
              labels: _.map(data?.onTimeOverTimeData, (s) => dayjs(s.date).format('DD/MM/YYYY')),
              datasets: [
                {
                  label: t('on_time_performance'),
                  data: _.map(data?.onTimeOverTimeData, (s) => s.punctuality * 100),
                  backgroundColor: '#188c4baa',
                  pointStyle: 'circle',
                  pointRadius: 6,
                  fill: true
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              scales: {
                y: {
                  ticks: {
                    beginAtZero: true,
                    callback: (value, index, ticks) => percentage(value)
                  }
                },
              },
              responsive: true,
              plugins: {
                zoom: {
                  pan: {
                    enabled: true,
                    mode: 'x',
                  },
                  zoom: {
                    wheel: {
                      enabled: true,
                    },
                    pinch: {
                      enabled: true
                    },
                    mode: 'x',
                  }
                },
                datalabels: {
                  display: true,
                  offset: -30,
                  align: 'start',
                  color: 'white',
                  backgroundColor: '#38b349',
                  borderColor: 'blue',
                  borderRadius: 5,
                  formatter: (value, context) => percentage(value),
                  labels: {
                    title: {
                      font: {
                        weight: 'regular'
                      }
                    },
                  }
                },
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
              },
            }}
          />
        </Spin>
      </div>
      <div className="vh-50 bg-white shadow-lg p-2 col-span-1 md:col-span-2 lg:col-span-6">
        <Spin spinning={dashboardAnalyticsHeader.loading}>
          <Bar
            height={350}
            data={{
              labels: _.map(data?.scheduledVsOperatedTripsData, (s) => dayjs(s.date).format('DD/MM/YYYY')),
              datasets: [
                {
                  key: `operated-${data?.date}`,
                  label: t('trips_operated'),
                  borderColor: '#38b349',
                  backgroundColor: '#38b349aa',
                  data: _.map(data?.scheduledVsOperatedTripsData, (x) => x.operatedTotal),
                },
                {
                  key: `scheduled-${data?.date}`,
                  label: t('trips_scheduled'),
                  borderColor: '#38b349',
                  backgroundColor: '#38b34933',
                  data: _.map(data?.scheduledVsOperatedTripsData, (x) => x.scheduledTotal),
                }
              ],
            }}
            options={{
              maintainAspectRatio: false,
              scales: {
                y: {
                  ticks: {
                    beginAtZero: true,
                    callback: (value, index, ticks) => thousand(value)
                  },
                  stacked: false
                },
                x: {
                  stacked: true
                }
              },
              responsive: true,
              plugins: {
                zoom: {
                  pan: {
                    enabled: true,
                    mode: 'x',
                  },
                  zoom: {
                    wheel: {
                      enabled: true,
                    },
                    pinch: {
                      enabled: true
                    },
                    mode: 'x',
                  }
                },
                legend: {
                  position: 'top',
                },
                title: {
                  display: false,
                },
                datalabels: {
                  anchor: 'end',
                  offset: 8,
                  rotation: 330,
                  color: 'black',
                  formatter: (value, context) => thousand(value),
                  labels: {
                    title: {
                      font: {
                        weight: 'regular'
                      }
                    }
                  }
                }
              },
            }}
          />
        </Spin>
      </div>
      <div className="bg-white shadow-lg p-2 col-span-1 md:col-span-2 lg:col-span-4">
        <Spin spinning={dashboardAnalyticsHeader.loading}>
          <div>
            <Segmented
              size="medium"
              block
              className="w-full mt-4 space-x-2"
              options={[t('group_by_operator'), t('group_by_route')]}
              value={groupedByOperator ? t('group_by_operator') : t('group_by_route')}
              onChange={(value) => {
                setGroupedByOperator(value === t('group_by_operator'));
              }}
            />
          </div>
          <div className="mt-2">
            <Bar
              height={350}
              data={{
                labels: _.map(groupedByOperator ? data?.cancelledTripsByOperatorData : data?.cancelledTripsByRouteData, (s) => s.label),
                datasets: [
                  {
                    key: `current-${data?.label}`,
                    label: t('cancelled_trips'),
                    borderColor: '#0e733b',
                    backgroundColor: '#0e733baa',
                    data: _.map(groupedByOperator ? data?.cancelledTripsByOperatorData : data?.cancelledTripsByRouteData, (x) => x.cancellations),
                  }
                ],
              }}
              options={{
                maintainAspectRatio: false,
                scales: {
                  y: {
                    ticks: {
                      beginAtZero: true,
                      callback: (value, index, ticks) => thousand(value)
                    }
                  },
                },
                responsive: true,
                plugins: {
                  zoom: {
                    pan: {
                      enabled: true,
                      mode: 'x',
                    },
                    zoom: {
                      wheel: {
                        enabled: true,
                      },
                      pinch: {
                        enabled: true
                      },
                      mode: 'x',
                    }
                  },
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: false,
                  },
                  datalabels: {
                    anchor: 'end',
                    offset: 8,
                    rotation: 330,
                    color: 'black',
                    formatter: (value, context) => thousand(value),
                    labels: {
                      title: {
                        font: {
                          weight: 'regular'
                        }
                      }
                    }
                  }
                },
              }}
            />
          </div>
        </Spin>
      </div>
      <div className="bg-white shadow-lg p-2 col-span-1 md:col-span-2 lg:col-span-2">
        {!dashboardAnalyticsHeader.loading && (
          <Doughnut
            data={{
              labels: [t('available_fleet'), t('not_available')],
              datasets: [
                {
                  label: t('percentage'),
                  backgroundColor: ['#00a16baa', '#00a16b33'],
                  borderWidth: 1,
                  data: [data?.fleetAvailableKpi * 100, (1 - data?.fleetAvailableKpi) * 100],
                }
              ],
            }}
            options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'top',
                  labels: {
                    font: {
                      size: 20
                    }
                  }
                },
                title: {
                  display: false,
                },
                datalabels: {
                  color: 'black',
                  formatter: (value, context) => percentage(value),
                  labels: {
                    value: {
                      font: {
                        size: 20
                      }
                    }
                  }
                }
              },
            }}
          />
        )}
      </div>
      <div className="bg-white shadow-lg p-2 col-span-1 md:col-span-2 lg:col-span-6">
        <Spin spinning={dashboardAnalyticsHeader.loading}>
          <p className="text-xl">{t('problematic_routes')}</p>
          <Table
            bordered
            locale={{
              emptyText: t('no_data')
            }}
            pagination={false}
            dataSource={data?.problematicRoutes}
            columns={[
              {
                title: '',
                align: 'left',
                className: 'text-sm w-full md:hidden',
                render: (row) => (
                  <div className="grid grid-cols-2 gap-2 w-full text-center">
                    <div className="col-span-2">
                      <p>{row?.operatorName ?? row.operatorId}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="font-bold">{row?.routeName ?? row.routeId}</p>
                    </div>
                    <div className="col-span-1 text-left">
                      <p className="font-bold">{t('operated_trips')}</p>
                      <p>{thousand(row.operatedTripsCount)}</p>
                    </div>
                    <div className="col-span-1 text-right">
                      <p className="font-bold">{t('cancelled_trips')}</p>
                      <p>{thousand(row.cancelledTrips)}</p>
                    </div>
                    <div className="col-span-1 text-left">
                      <p className="font-bold">{t('scheduled_trips')}</p>
                      <p>{thousand(row.plannedOperatedTripsCount)}</p>
                    </div>
                    <div className="col-span-1 text-right">
                      <p className="font-bold">{t('scheduled_trips_operated')}</p>
                      <p>{percentage(row.schedulesTripsOperated * 100)}</p>
                    </div>
                    <div className="col-span-1 text-left">
                      <p className="font-bold">{t('on_time_performance')}</p>
                      <p>{percentage(row.onTimePerformance * 100)}</p>
                    </div>
                    <div className="col-span-1 text-right">
                      <p className="font-bold">{t('average_delay')} {t('mins')}</p>
                      <p>{thousand(row.averageDelayMinutes)}</p>
                    </div>
                  </div>
                )
              },
              {
                title: t('operator'),
                key: 'operator',
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{row.operatorName ?? row.operatorId}</span>
                ),
              },
              {
                title: t('route'),
                key: 'route',
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{row.routeName ?? row.routeId}</span>
                ),
              },
              {
                title: t('operated_trips'),
                key: 'operated',
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{thousand(row.operatedTripsCount)}</span>
                ),
              },
              {
                title: t('scheduled_trips'),
                key: 'scheduled',
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{thousand(row.plannedOperatedTripsCount)}</span>
                ),
              },
              {
                title: t('cancelled_trips'),
                key: 'cancelled',
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{thousand(row.cancelledTrips)}</span>
                ),
              },
              {
                title: t('scheduled_trips_operated'),
                key: 'scheduled_operated',
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{percentage(row.schedulesTripsOperated * 100)}</span>
                ),
              },
              {
                title: t('on_time_performance'),
                key: 'on_time_perf',
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{percentage(row.onTimePerformance * 100)}</span>
                ),
              },
              {
                title: `${t('average_delay')} (${t('mins')})`,
                key: 'cancelled',
                align: 'center',
                className: 'text-sm hidden md:table-cell',
                render: (row) => (
                  <span>{thousand(row.averageDelayMinutes)}</span>
                ),
              },
            ]}
          />
        </Spin>
      </div>
    </div>
  );
}

export default AuthorityView;
